import React from 'react';
import Button from '../Button';
import LoadingButton from '../BulkOperations/_Shared/BulkActionButton/LoadingButton';
import { GenericDialogue } from '../Dialogue/Dialogue';
import Row from '../Row';
import '../SuiteForm/Components/SuiteAreas/SuiteAreas.scss';
import './PropertyForm.scss';
import {
  usePropertyFormOrchestrator,
  UsePropertyFormOrchestratorReturn,
} from './hooks/usePropertyFormOrchestrator/usePropertyFormOrchestrator';
import PropertyBasicsSection from './sections/PropertyBasicsSection';
import PropertyAddressSection from './sections/PropertyAddressSection';
import ModulePermissionsSection from './sections/ModulePermissionsSection';
import PropertyImageSection from './sections/PropertyImageSection';
import ConstructionStagesSection from './sections/ConstructionStagesSection';
import Col from '../Col';

interface PropertyFormProps {
  onClose: () => void;
  onSaveProperty: (
    data: any,
    action: 'SaveAndClose' | 'SaveAndAdd'
  ) => Promise<{ ok: boolean }>;
  tabList: any[];
  initialData?: any;
  initialModulePermissions?: any[];
  onClearValidation?: () => void;
  onModulePermissionsChange?: (perms: any[]) => void;
  asDialog?: boolean;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const PropertyForm = ({
  onClose,
  onSaveProperty,
  tabList,
  initialData,
  initialModulePermissions,
  onClearValidation,
  onModulePermissionsChange,
  asDialog = true,
}: PropertyFormProps) => {
  const {
    propertyFormData,
    propertyManagers,
    propertyStages,
    propertyStatuses,
    selectedModulePermissions,
    loader,
    user,

    setFormData,
    clearErrorField,

    errors,
    disableButton,
    isSubmitting,
    setSequence,

    validateAndSave,
    onAddNewConstructionStatus,
    onhandleStatusUpdate,
    onhandleStatusDelete,
    onChangeRank,
    onSetModules,
  }: UsePropertyFormOrchestratorReturn = usePropertyFormOrchestrator({
    initialData,
    initialModulePermissions,
    onSaveProperty,
    onClose,
    onClearValidation,
    onModulePermissionsChange,
  });

  const userRoleName: string | undefined = user?.roles?.[0]?.role?.name;

  const filteredPropertyManagers: any[] =
    userRoleName === 'Property Developer'
      ? propertyManagers.filter((item: any) => item.id === user.id)
      : propertyManagers;

  const isModulePermissionsSelected: boolean =
    selectedModulePermissions.length > 0;

  const filterData: any[] =
    propertyFormData?.constructionStages?.length > 0
      ? propertyFormData.constructionStages.filter((cs: any) => cs.new)
      : [];

  const assignedManagerId: string | undefined =
    propertyFormData.propertyManager?.id ?? propertyFormData.propertyManagerId;

  const hasPropertyManager: boolean =
    Boolean(assignedManagerId) && assignedManagerId !== EMPTY_GUID;

  const isAddAnotherDisabled: boolean =
    !propertyFormData.name ||
    !propertyFormData.addressLine1 ||
    !propertyFormData.city ||
    !propertyFormData.province ||
    !propertyFormData.postalCode ||
    !hasPropertyManager ||
    !isModulePermissionsSelected ||
    disableButton;

  const isSaveDisabled: boolean =
    !propertyFormData.name ||
    !propertyFormData.addressLine1 ||
    !propertyFormData.city ||
    !propertyFormData.province ||
    !propertyFormData.postalCode ||
    !isModulePermissionsSelected ||
    disableButton ||
    !hasPropertyManager;

  const title: string = (propertyFormData.id ? 'Edit' : 'Add a') + ' Property';

  const isSaving: boolean = isSubmitting || loader;

  const actions = (
    <>
      {!propertyFormData.id ? (
        <Button
          color="semantic"
          size="small"
          label="Save and Add Another"
          onClick={() => validateAndSave('SaveAndAdd')}
          disabled={isAddAnotherDisabled || isSaving}
        />
      ) : null}
      <LoadingButton
        actionText="Save Property"
        size="small"
        isSubmitting={isSaving}
        disabled={isSaveDisabled}
        onClick={() => validateAndSave('SaveAndClose')}
      />
    </>
  );

  const body = (
    <div className="property-form">
      <Row mdCenter>
        {/* Dialog constrains width via maxWidth, so fill it; inline (wizard) keeps
            the centered half-width column so it isn't edge-to-edge on screen. */}
        <Col xs="12" md={asDialog ? '12' : '6'}>
          <PropertyBasicsSection
            showHeading={asDialog}
            name={propertyFormData.name}
            propertyManagerId={propertyFormData.propertyManagerId}
            propertyManager={propertyFormData.propertyManager}
            errors={errors}
            filteredPropertyManagers={filteredPropertyManagers}
            onChange={setFormData}
          />
          <PropertyAddressSection
            addressLine1={propertyFormData.addressLine1}
            city={propertyFormData.city}
            province={propertyFormData.province}
            postalCode={propertyFormData.postalCode}
            errors={errors}
            onChange={setFormData}
            onClearErrorField={clearErrorField}
          />
          <ModulePermissionsSection
            tabList={tabList}
            selectedModulePermissions={selectedModulePermissions}
            onSetModules={onSetModules}
          />
          <PropertyImageSection
            selectedImage={propertyFormData.selectedImage}
            image={propertyFormData.image}
            onChange={setFormData}
          />
          <ConstructionStagesSection
            propertyFormData={propertyFormData}
            propertyStages={propertyStages}
            propertyStatuses={propertyStatuses}
            filterData={filterData}
            onChange={setFormData}
            onhandleStatusUpdate={onhandleStatusUpdate}
            onhandleStatusDelete={onhandleStatusDelete}
            setSequence={setSequence}
            onChangeRank={onChangeRank}
            onAddNewConstructionStatus={onAddNewConstructionStatus}
          />
        </Col>
      </Row>
    </div>
  );

  if (!asDialog) {
    return (
      <div className="property-form-inline">
        {body}
        <div className="property-form-inline-actions">{actions}</div>
      </div>
    );
  }

  return (
    <GenericDialogue
      isOpen
      onClose={onClose}
      title={title}
      footer={actions}
      maxWidth="80rem"
    >
      {body}
    </GenericDialogue>
  );
};

export default PropertyForm;
